/*
Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained

access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.
*/

import css from '../../css/loan-escrow-accruals-styles/HoaAccruals.module.css';
import { phoneNumberFormatter, epochToDate, formatDate } from '../../common/UtilityFunctions';
import { ImpoundPolicyListResponse, InsCompResponse, MiscPayeeInfoResponse } from '../../common/queryFunctions';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { NumericFormat } from 'react-number-format';
import { useAuthContext } from '../../contexts/AuthContext';
import useInsCompInfo from '../../common/hooks/useInsCompInfo';
import useMiscPayeeInfo from '../../common/hooks/useMiscPayeeInfo';

interface ComponentProps {
  hoaImpounds: Array<ImpoundPolicyListResponse>;
  compCodes: Set<string>;
  payeeNumbers: Set<string>;
}

const HoaAccruals: React.FC<ComponentProps> = (props) => {
  const { token } = useAuthContext();
  const [showPaidImpoundLines, setShowPaidImpoundLines] = useState<boolean>(false);
  const [hoaImpounds, setHoaImpounds] = useState<{ [key: string]: ImpoundPolicyListResponse[] }>({});
  const [halfDecades, setHalfDecades] = useState<Array<number>>([]);
  const [selectedHalfDecade, setSelectedHalfDecade] = useState<string>('');

  const {
    data: hoaPayeeInfo,
    status: hoaPayeeStatus,
    error: hoaPayeeErrors,
  } = useInsCompInfo(props.compCodes, token, 'hoaInsCompInfo');
  const {
    data: hoaMiscInfo,
    status: hoaMiscStatus,
    error: hoaMiscErrors,
  } = useMiscPayeeInfo(props.payeeNumbers, token, 'hoaMiscPayeeInfo');

  const flattenedHoaImpounds: Array<ImpoundPolicyListResponse> = useMemo(() => {
    let tempArray: Array<ImpoundPolicyListResponse> = props.hoaImpounds.flatMap((impList) => impList);
    tempArray.sort((a, b) => {
      //if impound types are the same, order by payment due date
      return a.renewalDte - b.renewalDte;
    });
    return tempArray;
  }, [props.hoaImpounds]);

  //this effect sorts hoa accruals by payment status and hald decade period of due date
  useEffect(() => {
    const indexes: { [key: string]: ImpoundPolicyListResponse[] } = { ['unpaid']: [] };
    let currentHalfDecade: number = 0;
    let currentHalfDecadeFirstIndex: number = 0;
    for (let i = 0; i < flattenedHoaImpounds.length; i++) {
      const currentHoaImpound: ImpoundPolicyListResponse = flattenedHoaImpounds[i];
      const year: number = epochToDate(currentHoaImpound.renewalDte).getFullYear();
      const halfDecade: number = Math.floor(year / 5) * 5;
      if (currentHalfDecade === 0) {
        currentHalfDecade = halfDecade;
      }
      if (halfDecade !== currentHalfDecade) {
        indexes[currentHalfDecade.toString()] = flattenedHoaImpounds.slice(currentHalfDecadeFirstIndex, i);
        if (!halfDecades.includes(currentHalfDecade)) {
          setHalfDecades([...halfDecades, currentHalfDecade]);
        }
        currentHalfDecade = halfDecade;
        currentHalfDecadeFirstIndex = i;
      }
      if (currentHoaImpound.servPremium > currentHoaImpound.servAmtPaid) {
        indexes['unpaid'] = [...indexes['unpaid'], currentHoaImpound];
      }
    }
    if (flattenedHoaImpounds.length !== 0) {
      indexes[currentHalfDecade.toString()] = flattenedHoaImpounds.slice(currentHalfDecadeFirstIndex);
      if (!halfDecades.includes(currentHalfDecade)) {
        setHalfDecades([...halfDecades, currentHalfDecade]);
      }
    }
    setHoaImpounds(indexes);
  }, [flattenedHoaImpounds, halfDecades]);

  //sets the selected half decade to the first half-decade upon population of the half-decade array
  useEffect(() => {
    if (halfDecades.length > 0) {
      halfDecades.sort((a, b) => a - b);
      setSelectedHalfDecade(halfDecades[0].toString());
    }
  }, [halfDecades, showPaidImpoundLines]);

  const handlePaidImpoundToggle = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.checked;
    setShowPaidImpoundLines(value);
  }, []);

  const handleHalfDecadeSelection = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const value: string = event.target.value;
    setSelectedHalfDecade(value);
  }, []);

  if (hoaPayeeStatus === 'pending' || hoaMiscStatus === 'pending') return <div>Loading...</div>;
  if ((hoaPayeeErrors ?? []).length > 0 || (hoaMiscErrors ?? []).length > 0)
    return (
      <>
        <h2>Encountered errors while obtaining HOA Accrual Information.</h2>
        <ul>
          {[...(hoaPayeeErrors ?? []), ...(hoaMiscErrors ?? [])].map((queryError, index) => {
            return <li key={index}>{queryError.message}</li>;
          })}
        </ul>
      </>
    );

  return (
    <>
      <form>
        <span>Show Fully Paid Impound Lines?</span>
        <input
          type='checkbox'
          name='showPaidImpoundLines'
          checked={showPaidImpoundLines}
          onChange={handlePaidImpoundToggle}
        ></input>
        {showPaidImpoundLines && halfDecades.length > 0 && (
          <div>
            <span>Select Half-Decade:</span>
            {halfDecades.map((halfDecade, index) => (
              <label key={index} className={css.radioLabel}>
                <input
                  type='radio'
                  value={halfDecade.toString()}
                  checked={selectedHalfDecade === halfDecade.toString()}
                  onChange={handleHalfDecadeSelection}
                ></input>
                {`${halfDecade.toString()} - ${(halfDecade + 4).toString()}`}
              </label>
            ))}
          </div>
        )}
      </form>
      <table className={css.hoaTable}>
        <caption className='h3 semibold'>HOA&nbsp;Accruals</caption>
        <thead>
          <tr>
            <th>Impound Type</th>
            <th>Payee</th>
            <th>Due Date</th>
            <th>Payment Due</th>
            <th>Sup. Payment Due</th>
            <th>Payment Status</th>
            <th>Policy No.</th>
            <th>Escrowed?</th>
            <th>Comment</th>
          </tr>
        </thead>
        <tbody>
          {Object.values(hoaImpounds).some((impounds) => Array.isArray(impounds) && impounds.length > 0) &&
            hoaImpounds[!showPaidImpoundLines ? 'unpaid' : selectedHalfDecade].map((impound, index) => {
              const payeeInfo: InsCompResponse = hoaPayeeInfo.find(
                (payee) => payee.compCode === impound.insuranceComp.replace(/-/g, ''),
              );

              const miscInfo: MiscPayeeInfoResponse = hoaMiscInfo.find(
                (payee) => payee.payeeNumber === impound.payeeNumber,
              );

              const paymentStatus: string =
                impound.servPremium <= impound.servAmtPaid && impound.servSupPremium <= impound.servSupAmtPaid
                  ? 'FULLY PAID'
                  : impound.servAmtPaid > 0 || impound.servSupAmtPaid > 0
                    ? 'PARTLY PAID'
                    : 'NOT PAID';

              return (
                <tr key={index}>
                  <td>{impound.impoundType}</td>
                  {payeeInfo ? ( //refer to company code info first if it exists, if not check the misc payee number, if netiher has data, return blank
                    <td>
                      {payeeInfo.name1}{' '}
                      {phoneNumberFormatter(payeeInfo.primaryContactPhone, payeeInfo.primaryContactPhoneExtension)}
                    </td>
                  ) : miscInfo ? (
                    <td>
                      {miscInfo.nameOne} {phoneNumberFormatter(miscInfo.phoneNo, '')}
                    </td>
                  ) : (
                    <td></td>
                  )}
                  {impound.renewalDte ? <td>{formatDate(epochToDate(impound.renewalDte))}</td> : <td></td>}
                  <td>
                    <NumericFormat
                      displayType='text'
                      value={impound.servPremium}
                      decimalScale={2}
                      fixedDecimalScale
                      prefix='$'
                      thousandSeparator
                    />
                  </td>
                  <td>
                    <NumericFormat
                      displayType='text'
                      value={impound.servSupPremium}
                      decimalScale={2}
                      fixedDecimalScale
                      prefix='$'
                      thousandSeparator
                    />
                  </td>
                  <td>{paymentStatus}</td>
                  <td>{impound.policyNum}</td>
                  <td>{impound.escrowed ? 'Yes' : 'No'}</td>
                  <td>{impound.comment}</td>
                </tr>
              );
            })}
        </tbody>
      </table>
    </>
  );
};
export default HoaAccruals;
